import * as React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Plus from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Chip,
  Divider,
  Icon,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@mui/material';

import { blueGrey } from '@mui/material/colors';
import {
  getColorForItem,
  getIconForItem,
} from '../../../utils/iconHelper';
import AddEditTagModal from './components/AddEditTagModal';

const TagsAutocomplete = ({
  onSelect,
  value,
  size,
  limitTags,
  editMode,
  label,
  includeTaskTypes,
  ...rest
}) => {
  // React state
  const [search, setSearch] = React.useState('');
  const [openTagModal, setOpenTagModal] = React.useState(false);
  const [selectedTag, setSelectedTag] = React.useState(null);

  // Redux state
  const { tags } = useSelector((state) => state.tag);
  const { tagTypes } = useSelector((state) => state.tagType);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);

  /*
    Filter on tags of the "Tags" type. If option to include task types in true,
    include the horse task and barn task type tags
  */
  const tagOptions = tags.filter((t) => (t.tagType.id === selectedBarn.barnMetadata.tagsTagTypeId
    || t.tagType.id === selectedBarn.barnMetadata.healthTagTypeId)
    || (includeTaskTypes && (t.id === selectedBarn.barnMetadata.barnTaskTypeTagId || t.id === selectedBarn.barnMetadata.horseTaskTypeTagId)))

  const handleAdd = (newTag) => {
    setOpenTagModal(false);
    setSelectedTag(null);
    onSelect({}, [...value, newTag])
  }
  const handleEdit = () => {
    setOpenTagModal(false);
    setSelectedTag(null);
  }

  const gettagTypeForId = (id) => tagTypes.find((tt) => tt.id === id) || { name: 'unknown' }

  return (
    <>
      <Autocomplete
        id="barn-horses-select-autocomplete"
        multiple
        onChange={onSelect}
        size={size}
        disableClearable
        disableCloseOnSelect
        limitTags={limitTags}
        filterOptions={() => [...(tagOptions).filter((tag) => tag.name.toLowerCase().indexOf(search.toLowerCase()) >= 0
          && (!value || !Array.isArray(value) || value.filter((v) => v.id === tag.id).length === 0))
          .sort((a, b) => a.tagType.id - b.tagType.id || -b.name.localeCompare(a.name)),
        ...(editMode
          ? [{
            name: 'Add New Tag',
            id: -1,
            tagType: { id: -1 },
          }]
          : []),
        ]}
        options={tags.sort((a, b) => a.tagType.id - b.tagType.id || -b.name.localeCompare(a.name))}
        value={value}
        isOptionEqualToValue={(option, valueCompare) => option.id === valueCompare.id}
        getOptionLabel={(option) => option.name || ''}
        groupBy={(option) => parseInt(option.tagType.id, 10)}
        {...rest}
        renderGroup={(params) => (
          <li key={params.group}>
            <ListItem sx={{ backgroundColor: blueGrey[50] }}>
              <ListItemIcon>
                <Icon style={{ color: getColorForItem(tagTypes, params.group) }}>
                  {getIconForItem(tagTypes, params.group)}
                </Icon>
              </ListItemIcon>
              <ListItemText
                primary={params.group > 0
                  ? gettagTypeForId(params.group).name
                  : "Don't see the right tag?"}
                primaryTypographyProps={{ fontWeight: 700 }}
              />
            </ListItem>
            <Divider />
            <Box>
              {params.children}
            </Box>
          </li>
        )}
        renderOption={(props, option) => (option.id > 0
          ? (
            <MenuItem
              {...props}
              sx={{
                width: '100%',
                display: 'inline-flex',
                justifyContent: 'space-between',
                ml: 2,
              }}
              key={`tag-chip-option-autocomplete-${option.id}`}
            >
              <ListItemIcon
                sx={{
                  w: 22,
                  px: 0,
                  mx: 0,
                }}
              >
                <Icon
                  style={{
                    fontSize: 18,
                    color: getColorForItem(tagTypes, option.tagType.id),
                  }}
                >
                  {getIconForItem(tagTypes, option.tagType.id)}
                </Icon>
              </ListItemIcon>
              <ListItemText>
                {option.name}
              </ListItemText>
              {(editMode && option.id && option.id !== selectedBarn.barnMetadata.farrierTagId)
                 && (
                   <IconButton
                     size="small"
                     onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedTag(option); setOpenTagModal(true); }}
                   ><Edit />
                   </IconButton>
                 )}
            </MenuItem>
          )
          : (
            <Box
              sx={{
                ml: 4,
                my: 1,
              }}
            >
              <Button variant="contained" size="small" startIcon={<Plus />} onClick={() => { setOpenTagModal(true); }}>Create New Tag</Button>
            </Box>
          ))}
        renderTags={(tagValue, getTagProps) => tagValue.sort((a, b) => (((
          a.tagType.id === selectedBarn.barnMetadata.tagsTagTypeId || a.tagType.id === selectedBarn.barnMetadata.healthTagTypeId)
            && b.tagType.id !== selectedBarn.barnMetadata.tagsTagTypeId)
          ? 1
          : -1)).map((option, index) => (
            <Chip
              key={`tag-chip-autocomplete-${option.id}`}
              {...getTagProps({ index })}
              size={size}
              icon={(
                <Icon style={{ color: getColorForItem(tagTypes, option.tagType.id) }}>
                  {getIconForItem(tagTypes, option.tagType.id)}
                </Icon>
              )}
              label={option.name}
              disabled={!selectedBarn || !selectedBarn.barnMetadata || option.tagType.id === selectedBarn.barnMetadata.taskTypeTagTypeId}
              onDelete={!(!selectedBarn || !selectedBarn.barnMetadata || option.tagType.id === selectedBarn.barnMetadata.taskTypeTagTypeId)
                ? getTagProps({ index }).onDelete
                : undefined}
            />
        ))}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            fullWidth
            onChange={(e) => { setSearch(e.target.value); }}
          />
        )}
      />
      <AddEditTagModal
        open={openTagModal}
        onClose={() => setOpenTagModal(false)}
        tag={selectedTag}
        onAdd={handleAdd}
        onEdit={handleEdit}
      />
    </>
  );
};

TagsAutocomplete.propTypes = {
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.any,
  size: PropTypes.string,
  limitTags: PropTypes.number,
  editMode: PropTypes.bool,
  label: PropTypes.string,
  includeTaskTypes: PropTypes.bool,
};

TagsAutocomplete.defaultProps = {
  value: null,
  size: 'small',
  limitTags: 5,
  editMode: true,
  label: 'Tags',
  includeTaskTypes: false,
};

export default TagsAutocomplete;
